import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Bike, Footprints, Baby, ArrowRight, MapPin, CalendarDays, Clock, ChevronRight, Medal, Phone, FileText, ShieldCheck, Globe } from "lucide-react";
import EventInfo from "@/components/EventInfo";
import ResultsSection from "@/components/ResultsSection";
import { Button } from "@/components/ui/button";
import { api, absUrl } from "@/lib/api";
import { brl, TIERS } from "@/lib/format";
import RegisterDialog from "@/components/RegisterDialog";

const SPORT_META = {
  CORRIDA: { icon: Footprints, label: "Corrida 5 KM", tag: "Runner" },
  CICLISMO: { icon: Bike, label: "Ciclismo 10 KM", tag: "Cyclist" },
  KIDS: { icon: Baby, label: "Corrida KIDS", tag: "Youth" },
};



function Countdown({ date, time }) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const target = new Date((date || "") + "T" + (time || "07:00") + ":00").getTime();
    const tick = () => {
      const diff = Math.max(target - Date.now(), 0);
      setT({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff / 3600000) % 24),
        m: Math.floor((diff / 60000) % 60),
        s: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [date, time]);
  const Box = ({ v, l }) => (
    <div className="text-center">
      <div className="font-display text-4xl sm:text-5xl font-black text-white tabular-nums">{String(v).padStart(2, "0")}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-400">{l}</div>
    </div>
  );
  return (
    <div className="flex gap-4 sm:gap-6" data-testid="event-countdown">
      <Box v={t.d} l="Dias" /><Box v={t.h} l="Horas" /><Box v={t.m} l="Min" /><Box v={t.s} l="Seg" />
    </div>
  );
}

export default function Landing() {
  const [data, setData] = useState(null);
  const [openReg, setOpenReg] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/public/event").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const event = data?.event;
  const cats = data?.categories || [];
  const sponsors = data?.sponsors || [];

  // group categories by sport for modality cards
  const bySport = {};
  cats.forEach((c) => {
    if (!bySport[c.sport_type]) bySport[c.sport_type] = { price: c.price, remaining: 0, subs: [] };
    bySport[c.sport_type].remaining += c.remaining;
    bySport[c.sport_type].price = Math.min(bySport[c.sport_type].price, c.price);
    bySport[c.sport_type].subs.push(c);
  });

  return (
    <div className="min-h-screen bg-ink text-white">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-40 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center p-1"><img src="/logo-cemoc.png" alt="CEMOC RUN" className="w-full h-full object-contain" /></div>
            <span className="font-display text-xl sm:text-2xl font-black tracking-tight uppercase whitespace-nowrap">CEMOC RUN</span>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="nav-admin-btn" variant="ghost" onClick={() => navigate("/login")}
              className="hidden sm:inline-flex text-slate-400 hover:text-white hover:bg-white/5 text-xs uppercase tracking-wide"><ShieldCheck className="w-4 h-4 mr-1" /> Admin</Button>
            <Button data-testid="nav-login-btn" variant="ghost" onClick={() => navigate("/login")}
              className="text-slate-200 hover:text-white hover:bg-white/5">Já sou Atleta</Button>
            <Button data-testid="nav-register-btn" onClick={() => setOpenReg(true)}
              className="bg-brand hover:bg-brand-hover font-bold uppercase tracking-wide">Inscreva-se</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center pt-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/hero-cemoc-v2.jpg" alt="Atleta 1ª CEMOC RUN" className="w-full h-full object-cover object-[50%_6%] lg:translate-x-[10%]" data-testid="hero-runner" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/55 to-ink/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/40" />
          <div className="absolute inset-0 grain opacity-60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 py-20 w-full">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-brand/40 bg-brand/10 px-4 py-1.5 text-xs uppercase tracking-widest text-brand font-bold mb-6">
              <Medal className="w-3.5 h-3.5" /> Corrida • Ciclismo • Kids
            </div>
            <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl font-black uppercase leading-[0.9] tracking-tight max-w-3xl">
              1ª CEMOC RUN <span className="text-brand">COARI 2026</span>
            </h1>
            <p className="mt-3 text-neon uppercase tracking-widest text-sm font-bold">R$ 10 MIL em premiação • Vagas limitadas • Sorteio de brindes</p>
            {event?.event_date && (
              <div data-testid="hero-datetime" className="mt-5 inline-flex items-center gap-3 rounded-2xl border-2 border-neon/70 bg-ink/60 px-5 py-3 backdrop-blur">
                <CalendarDays className="w-7 h-7 text-neon" />
                <span className="font-display text-3xl sm:text-4xl font-black tabular-nums">
                  {new Date(event.event_date + "T00:00:00").toLocaleDateString("pt-BR")} <span className="text-neon">•</span> {event.event_time || "17:30"}h
                </span>
              </div>
            )}
            <p className="mt-5 text-lg text-slate-300 max-w-xl">
              Supere seus limites. Inscreva-se, pague via PIX e garanta seu número de peito.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <Button data-testid="hero-register-btn" size="lg" onClick={() => setOpenReg(true)}
                className="bg-brand hover:bg-brand-hover font-bold uppercase tracking-wide text-base h-12 px-8">
                Inscreva-se Agora <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              {event?.event_date && <Countdown date={event.event_date} time={event.event_time} />}
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-400">
              <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-brand" /> {event?.event_date ? new Date(event.event_date + "T00:00:00").toLocaleDateString("pt-BR") : "-"}</span>
              <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-brand" /> {event?.pix_city || "Local do evento"}</span>
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-brand" /> Largada às {event?.event_time || "17:30"}h</span>
              {event?.contact_phone && <span className="flex items-center gap-1.5" data-testid="hero-phone"><Phone className="w-4 h-4 text-brand" /> {event.contact_phone}</span>}
              {event?.site_url && <span className="flex items-center gap-1.5 font-bold text-neon" data-testid="hero-site"><Globe className="w-4 h-4" /> {event.site_url}</span>}
            </div>
          </motion.div>
          <motion.img src="/selo-15-anos.png" alt="15 anos Clínica CEMOC"
            initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 0.6 }}
            className="hidden lg:block absolute right-8 bottom-10 w-36 drop-shadow-2xl" data-testid="hero-seal" />
        </div>
      </section>

      {/* Patrocínio Master Ouro em destaque */}
      {sponsors.some((s) => s.tier === "MASTER_OURO") && (
        <section className="relative border-y border-amber-400/30 bg-gradient-to-b from-amber-400/10 via-surface/60 to-ink py-10 sm:py-14 overflow-hidden" data-testid="master-sponsors-band">
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[600px] h-48 rounded-full bg-amber-400/20 blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-8">
            <div className="flex items-center justify-center gap-3 mb-8">
              <span className="h-px w-10 sm:w-24 bg-amber-400/60" />
              <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold uppercase tracking-[0.3em] text-amber-300">
                <Medal className="w-4 h-4" /> Patrocínio Master Ouro
              </span>
              <span className="h-px w-10 sm:w-24 bg-amber-400/60" />
            </div>
            <div className="flex flex-wrap justify-center gap-6 sm:gap-10">
              {sponsors.filter((s) => s.tier === "MASTER_OURO").map((s, i) => (
                <motion.div key={s.id} data-testid={`master-sponsor-${s.id}`}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15, duration: 0.5 }}
                  className="rounded-3xl bg-white border-4 border-amber-400 shadow-[0_0_40px_rgba(251,191,36,0.35)] px-8 py-6 flex flex-col items-center gap-3 w-full sm:w-[360px] transition-transform hover:-translate-y-1">
                  {s.logo_view_url ? (
                    <img src={absUrl(s.logo_view_url)} alt={s.company_name} className="h-32 sm:h-40 w-full object-contain" />
                  ) : (
                    <div className="h-32 w-32 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center font-display font-black text-5xl">{s.company_name[0]}</div>
                  )}
                  <span className="font-display font-black uppercase text-ink text-lg text-center">{s.company_name}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Modalidades */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-20 sm:py-28">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-brand font-bold uppercase tracking-widest text-sm">Escolha sua prova</p>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight">Modalidades</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {["CORRIDA", "CICLISMO", "KIDS"].filter((s) => bySport[s]).map((s, i) => {
            const meta = SPORT_META[s];
            const info = bySport[s];
            const Icon = meta.icon;
            const totalSlots = info.subs.reduce((a, c) => a + c.slots_total, 0);
            const pct = Math.round(((totalSlots - info.remaining) / (totalSlots || 1)) * 100);
            return (
              <motion.div key={s} data-testid={`modality-card-${s}`}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group relative rounded-2xl bg-surface border border-white/10 p-6 hover:border-brand/50 transition-all overflow-hidden">
                <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-brand/10 group-hover:bg-brand/20 transition-colors" />
                <div className="relative">
                  <div className="w-14 h-14 rounded-xl bg-brand/15 border border-brand/30 flex items-center justify-center mb-5">
                    <Icon className="w-7 h-7 text-brand" />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-neon font-bold">{meta.tag}</span>
                  <h3 className="font-display text-2xl font-bold uppercase mt-1">{meta.label}</h3>
                  <p className="text-slate-400 text-sm mt-2">
                    {s === "KIDS" ? "Mista • 200 m • 6 a 13 anos • Nº K-01 a K-99" : s === "CICLISMO" ? "Maiores de 18 • CM 700–799 • CF 800–899 • Capacete obrigatório" : "Faixas: 14–29 • 30–39 • 40–49 • 50+ (M1–M4 / F1–F4)"}
                  </p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-slate-400">A partir de</span>
                    <span className="font-mono font-bold text-brand text-lg">{brl(info.price)}</span>
                  </div>
                  <div className="mt-3">
                    <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full bg-brand" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">{info.remaining} vagas restantes</p>
                  </div>
                  <Button data-testid={`modality-register-${s}`} onClick={() => setOpenReg(true)}
                    className="w-full mt-5 bg-white/5 hover:bg-brand hover:text-white border border-white/10 font-bold uppercase tracking-wide transition-colors">
                    Inscrever <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      <EventInfo event={event} />

      {/* Info geral */}
      {event && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { t: "Cronograma", v: event.schedule, i: Clock },
              { t: "Retirada de Kits", v: event.kit_info, i: MapPin },
              { t: "Regulamento", v: event.regulation, i: Trophy, link: event.regulation_url },
            ].map((b) => (
              <div key={b.t} className="rounded-2xl bg-surface border border-white/10 p-6">
                <b.i className="w-6 h-6 text-brand mb-3" />
                <h4 className="font-display text-xl uppercase font-bold mb-2">{b.t}</h4>
                <p className="text-sm text-slate-400 leading-relaxed">{b.v}</p>
                {b.link && (
                  <a data-testid="regulation-pdf-btn" href={b.link} target="_blank" rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-hover px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors">
                    <FileText className="w-4 h-4" /> Baixar Regulamento Oficial (PDF)
                  </a>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <ResultsSection />

      {/* Sponsors */}
      {sponsors.length > 0 && (
        <section className="border-t border-white/10 bg-surface/40 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-8">
            <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-center mb-2">Patrocinadores</h2>
            <p className="text-center text-slate-400 text-sm mb-10">Quem faz o RUN CEMOC acontecer</p>
            {["MASTER_OURO", "PREMIUM_PRATA", "GOLD_BRONZE"].map((tier) => {
              const list = sponsors.filter((s) => s.tier === tier);
              if (!list.length) return null;
              const master = tier === "MASTER_OURO";
              return (
                <div key={tier} className="mb-10">
                  <div className="flex items-center gap-2 justify-center mb-5">
                    <span className={`text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full ${TIERS[tier].chip}`}>{TIERS[tier].label}</span>
                  </div>
                  <div className={`flex flex-wrap justify-center ${master ? "gap-6" : "gap-4"}`}>
                    {list.map((s) => (
                      <div key={s.id} data-testid={`sponsor-${s.id}`}
                        className={master
                          ? "rounded-3xl bg-white border-4 border-yellow-400/80 shadow-[0_0_40px_rgba(250,204,21,0.25)] px-10 py-8 flex flex-col items-center gap-4 w-full sm:w-[340px] transition-transform hover:-translate-y-1"
                          : `rounded-2xl bg-white border-2 ${TIERS[tier].ring} px-6 py-5 flex flex-col items-center gap-3 w-[200px] sm:w-[240px] transition-transform hover:-translate-y-1`}>
                        {s.logo_view_url ? (
                          <img src={absUrl(s.logo_view_url)} alt={s.company_name} className={master ? "h-28 max-w-full object-contain" : "h-20 max-w-full object-contain"} />
                        ) : (
                          <div className={`${master ? "w-24 h-24 text-4xl rounded-2xl" : "w-16 h-16 text-2xl rounded-xl"} flex items-center justify-center font-display font-black`} style={{ background: TIERS[tier].color + "22", color: TIERS[tier].color }}>
                            {s.company_name[0]}
                          </div>
                        )}
                        <div className="text-center">
                          <div className={master ? "font-display font-black text-2xl uppercase text-ink" : "font-display font-bold text-sm uppercase text-ink leading-tight"}>{s.company_name}</div>
                          <div className={`text-[11px] ${master ? "text-ink/60 uppercase tracking-widest font-bold" : "text-ink/50"}`}>{master ? "Patrocinador Master" : s.business_sector}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="border-t border-white/10 bg-surface/40 py-12" data-testid="realizacao-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 text-center">
          <p className="text-slate-400 font-bold uppercase tracking-widest text-xs mb-6">Realização</p>
          <div className="flex flex-wrap items-center justify-center gap-10">
            <img src="/logo-cemoc-clinica.png" alt="CEMOC Medicina e Diagnóstico" className="h-14 object-contain" />
            <div className="bg-white rounded-2xl p-2"><img src="/logo-cemoc.png" alt="1ª CEMOC RUN 2026" className="h-16 object-contain" /></div>
            <img src="/selo-15-anos.png" alt="15 anos Clínica CEMOC" className="h-20 object-contain rounded-xl" />
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-10 flex flex-col items-center gap-4 text-slate-500 text-sm">
        <div className="rounded-2xl bg-white px-6 py-3 shadow-lg shadow-black/30">
          <img src="/logo-cemoc-clinica.png" alt="CEMOC Medicina e Diagnóstico" className="h-14 sm:h-16 object-contain" data-testid="footer-logo" />
        </div>
        <span>1ª CEMOC RUN 2026 — Realização: Clínica CEMOC, 15 anos cuidando de você</span>
        <button data-testid="footer-admin-link" onClick={() => navigate("/login")} className="text-xs text-slate-500 hover:text-brand underline underline-offset-4">Área administrativa (Gestor / Assistente)</button>
      </footer>

      <RegisterDialog open={openReg} onOpenChange={setOpenReg} onDone={load} />
    </div>
  );
}
